import { useRef } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { Star, Zap, ShieldCheck, BookOpen, MoonStar } from "lucide-react";
import { Starfield } from "./Starfield";
import { PRODUCT, liveDownloadCount } from "@/lib/data";
import { track } from "@/lib/analytics";

const LINES = ["FALL ASLEEP IN MINUTES.", "NOT HOURS.", "GUARANTEED."];

function MaskedLine({ text, index }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${index === 1 ? "text-gradient" : ""}`}
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay: 0.35 + index * 0.18, ease: [0.22, 1, 0.36, 1] }}
      >
        {text}
      </motion.span>
    </span>
  );
}

export function Hero({ onBuy }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const orbX = useTransform(mx, (v) => v * 34);
  const orbY = useTransform(my, (v) => v * 22);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top-hero"
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="relative overflow-hidden"
    >
      <Starfield className="opacity-70" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#07090e]/40 via-transparent to-[#07090e]" />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-4 pb-24 pt-16 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:pb-32 lg:pt-24">
        <div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-6 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-cyan-400/90"
            data-testid="hero-eyebrow"
          >
            ASTRA PRESS / THE 7-NIGHT SLEEP RESET — Instant PDF
          </motion.p>

          <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="hero-heading">
            {LINES.map((l, i) => (
              <MaskedLine key={l} text={l} index={i} />
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            The guided program that retrains your nights in one week — quiet a racing mind,
            fall asleep faster, and wake up actually rested. No pills, no apps, no subscriptions.
            One short chapter per night. Download it tonight, sleep different this week.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-buy-button"
              onClick={() => { track("InitiateCheckout", { value: PRODUCT.price, currency: "USD" }); onBuy(); }}
              className="group flex items-center gap-2.5 rounded-full bg-cyan-400 px-8 py-4 text-base font-bold text-[#07090e] shadow-[0_0_40px_rgba(56,189,248,0.35)] transition-transform hover:scale-105 active:scale-95"
            >
              <MoonStar className="h-5 w-5 transition-transform group-hover:-rotate-12" />
              Get Instant Access — ${PRODUCT.price}
            </button>
            <a
              data-testid="hero-cta-inside-button"
              href="#inside"
              className="flex items-center gap-2.5 rounded-full border border-white/20 px-8 py-4 text-base font-semibold text-white transition-colors hover:border-cyan-400/60 hover:text-cyan-300"
            >
              <BookOpen className="h-4 w-4" /> See What's Inside
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.45, duration: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-slate-400"
            data-testid="hero-social-proof"
          >
            <span className="flex items-center gap-1.5" data-testid="live-download-badge">
              <span className="h-1.5 w-1.5 animate-pulse-soft rounded-full bg-emerald-400" />
              <span className="font-semibold text-slate-300">{liveDownloadCount().toLocaleString()} downloads</span> and counting
            </span>
            <span className="flex items-center gap-1.5">
              <span className="flex text-amber-400">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}</span>
              <span className="font-semibold text-slate-300">4.9/5</span> · 4,800+ better sleepers
            </span>
            <span className="flex items-center gap-1.5"><Zap className="h-4 w-4 text-cyan-400" /> Instant download</span>
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-cyan-400" /> 30-night guarantee</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30, rotate: -2 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ delay: 0.7, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto hidden w-full max-w-sm lg:block"
          style={{ x: orbX, y: orbY }}
        >
          <div className="absolute -inset-8 rounded-[2.5rem] bg-indigo-500/10 blur-3xl" />
          <img
            src={PRODUCT.image}
            alt="The 7-Night Sleep Reset PDF guide — fall asleep faster and quiet a racing mind"
            className="relative w-full rounded-2xl border border-white/10 shadow-[0_40px_100px_rgba(0,0,0,0.7)]"
          />
          <div className="glass absolute -right-4 top-[52%] rounded-2xl border border-white/10 px-4 py-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Night 2</p>
            <p className="font-display text-lg font-bold text-white">Racing-Mind Fix</p>
          </div>
          <div className="glass absolute -left-4 bottom-[18%] rounded-2xl border border-white/10 px-4 py-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Bonus</p>
            <p className="font-display text-lg font-bold text-white">3AM Playbook</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
